/**
 * EventSeriesEditPage - Edit series properties (#1208, #2198)
 *
 * Route: /clubs/:slug/series/:seriesId/edit
 *
 * Saves via ONE `PATCH /events/:id` against the first upcoming instance with
 * `edit_scope: 'this_and_future'` — the backend fans the update out to every
 * upcoming instance and syncs the event_series metadata row (#2198). Past
 * instances are never modified by the fan-out; the previous client-side loop
 * that offered an "including past" scope was removed with the loop.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useClub } from '../hooks/useClubs';
import { useSeriesDetail, useUpdateSeries } from '../hooks/useEvents';
import { useAuth } from '../hooks/useAuth';
import { TopBar, Button, EmptyState, Toggle, Skeleton } from '../components';
import { LocationPickerModal, type LocationPickerResult } from '../components';
import { Save, MapPin, FileText, Shield, Repeat, Lock } from 'lucide-react';
import type { UpdateEventPayload } from '../lib/api/events';

export default function EventSeriesEditPage() {
  const { slug, seriesId } = useParams<{ slug: string; seriesId: string }>();
  const navigate = useNavigate();
  useAuth();

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationLat, setLocationLat] = useState<number | null>(null);
  const [locationLng, setLocationLng] = useState<number | null>(null);
  const [visibility, setVisibility] = useState<'public' | 'members_only'>('public');

  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // Fetch club details
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');

  // Fetch series detail
  const {
    data: seriesData,
    isLoading: seriesLoading,
    error: seriesError,
  } = useSeriesDetail(clubData?.club.id || '', seriesId || '');

  const loading = clubLoading || seriesLoading;
  const error = clubError || seriesError;
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isLeader = membership?.role === 'leader' || membership?.is_owner;

  const updateSeries = useUpdateSeries(clubData?.club.id ?? '');

  // Populate form when data loads
  useEffect(() => {
    if (seriesData) {
      const { series } = seriesData;
      /* eslint-disable react-hooks/set-state-in-effect -- Intentional: hydrate form state from fetched series (EventCheckInPage pattern) */
      setTitle(series.title);
      setDescription(series.description || '');
      setLocationName(series.location_name || '');
      setLocationAddress(series.location_address || '');
      setVisibility(series.visibility);
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [seriesData]);

  // Handle location selection
  const handleLocationSelect = (result: LocationPickerResult) => {
    setLocationName(result.name ?? '');
    setLocationAddress(result.address ?? '');
    setLocationLat(result.lat);
    setLocationLng(result.lng);
    setShowLocationPicker(false);
  };

  // Handle save (#2198): ONE PATCH against the first upcoming instance with
  // edit_scope 'this_and_future'. The backend fans out to every instance with
  // starts_at >= the anchor's, so past instances stay untouched (upcoming-only scope, not Rule 14 — #2200),
  // and a failure surfaces as a single error toast via useOroMutation instead
  // of being swallowed per-instance.
  const handleSave = () => {
    if (!seriesData || !seriesId) return;

    const anchor = seriesData.upcoming_instances[0];
    if (!anchor) {
      toast.error('No upcoming events to update');
      return;
    }

    // Optional text fields are sent trimmed, INCLUDING '' — the backend clears
    // a field on falsy ('' -> NULL) and only skips it when undefined. Sending
    // null fails Zod validation with a 400 (UpdateEventSchema fields are
    // .optional(), not .nullable()) — that was #2198 (d). Mirrors the mobile
    // buildSeriesEditPayload (#2199).
    const payload: UpdateEventPayload = {
      title: title.trim(),
      description: description.trim(),
      location_name: locationName.trim(),
      location_address: locationAddress.trim(),
      visibility,
    };

    if (locationLat !== null && locationLng !== null) {
      payload.location_lat = locationLat;
      payload.location_lng = locationLng;
    }

    updateSeries.mutate(
      { seriesId, anchorEventId: anchor.id, payload },
      { onSuccess: () => navigate(`/clubs/${slug}/series/${seriesId}`) },
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Edit Series" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-6">
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
              <Skeleton className="h-6 w-3/4 mb-4" />
              <Skeleton className="h-10 w-full mb-4" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !club || !slug || !seriesData || !isLeader) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Edit Series" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12">
            <EmptyState
              icon={<Lock className="w-12 h-12" />}
              title="Access Denied"
              message="You don't have permission to edit this series."
              actionLabel="Go Back"
              onAction={() => navigate(-1)}
            />
          </div>
        </div>
      </div>
    );
  }

  const { series } = seriesData;

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar
        title="Edit Series"
        subtitle={series.title}
        onBack={() => navigate(`/clubs/${slug}/series/${seriesId}`)}
      />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="px-4 py-6 space-y-6">
          {/* Series Info */}
          <div className="flex items-center gap-2 px-3 py-2 bg-accent/10 border border-accent/20 rounded-lg">
            <Repeat className="w-4 h-4 text-accent" />
            <span className="text-sm text-text-secondary">
              {series.recurrence_pattern} · {series.upcoming_count} upcoming
            </span>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Event Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Morning Run"
              maxLength={100}
              className="w-full bg-oro-surface border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details about this event series..."
              maxLength={2000}
              rows={4}
              className="w-full bg-oro-surface border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none resize-none"
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Location
            </label>
            <button
              type="button"
              onClick={() => setShowLocationPicker(true)}
              className="w-full bg-oro-surface border border-oro-border rounded-lg px-4 py-3 text-left hover:bg-oro-surface-alt transition-colors"
            >
              {locationName ? (
                <div>
                  <div className="text-text-primary">{locationName}</div>
                  {locationAddress && (
                    <div className="text-sm text-text-muted mt-1">{locationAddress}</div>
                  )}
                </div>
              ) : (
                <span className="text-text-muted">Select location</span>
              )}
            </button>
          </div>

          {/* Visibility */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-text-muted" />
                <div>
                  <div className="font-medium text-text-primary">Members Only</div>
                  <div className="text-sm text-text-muted">
                    Only club members can see this event
                  </div>
                </div>
              </div>
              <Toggle
                checked={visibility === 'members_only'}
                onChange={(checked) => setVisibility(checked ? 'members_only' : 'public')}
              />
            </div>
          </div>

          {/* Edit scope note (#2198): edits always apply to upcoming instances
              only. This series edit never modifies past events (upcoming-only
              scope, not Rule 14 — #2200), so the old past-inclusive scope
              selector was removed. */}
          <p className="text-sm text-text-muted">
            {/* series.upcoming_count, NOT upcoming_instances.length — the
                endpoint truncates the instance array to 10 while the save
                fans out to ALL upcoming instances (Frank round 3, PR #2396). */}
            Changes apply to {series.upcoming_count} upcoming event
            {series.upcoming_count === 1 ? '' : 's'}. Past events are never changed.
          </p>

          {/* Save Button */}
          <div className="pt-4 pb-20">
            <Button
              variant="primary"
              className="w-full"
              onClick={handleSave}
              disabled={updateSeries.isPending || !title.trim()}
            >
              <Save className="w-4 h-4 mr-2" />
              {updateSeries.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>

      {/* Location Picker Modal */}
      <LocationPickerModal
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onConfirm={handleLocationSelect}
        initialLocation={
          locationLat && locationLng
            ? { lat: locationLat, lng: locationLng }
            : undefined
        }
      />
    </div>
  );
}
